import React, { useContext, useState } from 'react';

import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import { AuthStore, UserStore } from 'stores';
import * as help from 'helpers';
import { ERRORS } from 'config';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import {
    Form,
    FormField,
    FormInput,
    Card,
    Loader,
    Alert,
    Button,
    Typography,
} from 'components';

const RecoverPasswordForm = () => {
    const authStore = useContext(AuthStore);
    const userStore = useContext(UserStore);
    const submitting = help.useBoolean(false);
    const submitted = help.useBoolean(false);
    const [error, setError] = useState('');

    const handleSubmitForm = async (e) => {
        e.preventDefault();

        if (!validate() || submitting.value) {
            return false;
        }

        submitting.setTrue();
        const data = getData();

        try {
            const res = await userStore.available(data.email);

            if (res.available) {
                setFormError('email', i18n._(ERRORS.email_not_found));
                submitting.setFalse();
                return;
            }

            authStore
                .recoverPassword(data.email)
                .then(() => {
                    setError('');
                    submitted.setTrue();
                })
                .catch(() => {
                    setError(i18n._(ERRORS.password_recover_generic));
                })
                .finally(() => submitting.setFalse());
        } catch (e) {
            console.log('YS', e);
        }
    };

    const onDidntGetEmailClick = () => {
        submitted.setFalse();
    };

    const formInitialData = {
        email: '',
    };

    const formRules = {
        email: 'required|email',
    };

    const formCustomMessages = {
        'email.required': i18n._(t`Please type your email address`),
    };

    const {
        form,
        errors,
        validate,
        getData,
        setError: setFormError,
    } = help.useValidatedForm(formInitialData, formRules, formCustomMessages);

    return (
        <Card noShadow mobileCard>
            {submitting.value && <Loader position="bottom" absolute={true} />}

            {error && (
                <Alert plain icon="alert" color="error">
                    {error}
                </Alert>
            )}

            {submitted.value ? (
                <React.Fragment>
                    <div>
                        <Typography variant="h5" spaceBottom="base">
                            <Trans>Check your inbox</Trans>
                        </Typography>

                        <Typography variant="bodys" spaceBottom="base">
                            <Trans>
                                We sent you a login link to{' '}
                                <strong>{form.email.input.value}</strong>. It
                                might take a few minutes to arrive, you may also
                                want to check your spam folder.
                            </Trans>
                        </Typography>

                        <Typography
                            linkType
                            variant="bodys"
                            onClick={onDidntGetEmailClick}
                        >
                            <Trans>I didn't get the email</Trans>
                        </Typography>
                    </div>
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <div>
                        <Typography variant="h5" spaceBottom="base">
                            <Trans>Reset your password</Trans>
                        </Typography>

                        <Typography variant="bodys">
                            <Trans>
                                Please enter the email address associated with
                                your account, and we'll email you a link to
                                reset your password.
                            </Trans>
                        </Typography>
                    </div>

                    <Form onSubmit={handleSubmitForm}>
                        <FormField
                            label={i18n._(t`Email address`)}
                            labelProps={{ htmlFor: 'form_email' }}
                            error={errors.email}
                        >
                            <FormInput
                                {...form.email.input}
                                placeholder=""
                                id="form_email"
                                disabled={submitting.value}
                                tabIndex="1"
                                autoComplete="email"
                                type="email"
                                inputMode="email"
                            />
                        </FormField>

                        <Button
                            data-action="submit"
                            type="submit"
                            theme={!submitting.value ? 'primary' : 'inactive'}
                            spaceTop="medium"
                            block
                        >
                            <Trans>Send reset link</Trans>
                        </Button>
                    </Form>
                </React.Fragment>
            )}
        </Card>
    );
};

RecoverPasswordForm.defaultProps = {};
RecoverPasswordForm.propTypes = {};

export default withRouter(observer(RecoverPasswordForm));
